import { useBreakpointValueGS } from 'hooks/useBreakpointValueGS';
import React from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from 'store/index';
import { AgeRatingDefinitions } from 'utils/ageRating';
import { calcSizeForDevice } from 'utils/index';

import { useAgePreference } from './useAgePreference';

import BandHeaderLogo from 'assets/Header/logoHeader.png';

import { BaseModal, ModalProps } from 'components/Modal';
import Rating from 'components/Rating';
import IsLoading from 'components/isLoading';

type ModalConfirmExitKidsProps = Partial<ModalProps> & { toggleModal: () => void };

const ModalAgeRating: React.FC<ModalConfirmExitKidsProps> = ({ toggleModal, ...rest }) => {
  const { ageRating: savedAgeRatingPreference } = useSelector((state: RootState) => state.profile);
  const {
    isAgeRatingKeyActive,
    availableAgeRatingKeys,
    setAgeRatingPreference,
    commitAgeRatingPreference,
    isAgeRatingPreferenceLoaded,
    ageRatingPreferenceHelperText,
  } = useAgePreference({ toggleModal, isOpen: rest.isOpen });

  const flexDirectionContainer = useBreakpointValueGS({ lg: 'column', base: 'column-reverse' });
  const flexDirectionView = useBreakpointValueGS({ lg: 'row', base: 'column' });
  const flexDirectionItem = useBreakpointValueGS({ lg: 'column', base: 'row' });
  const alignItemsView = useBreakpointValueGS({ lg: 'flex-start', base: 'center' });
  const justifyContentView = useBreakpointValueGS({ lg: 'center', base: 'flex-start' });
  const widthAbsoluteView = useBreakpointValueGS({ lg: '100%', base: 5 });
  const heightAbsoluteView = useBreakpointValueGS({ lg: calcSizeForDevice(5, 10), base: '100%' });
  const topAbsoluteView = useBreakpointValueGS({ lg: calcSizeForDevice(40, 55), base: '-100%' });
  const paddingTopAbsoluteView = useBreakpointValueGS({ lg: 0, base: 75 });
  const leftAbsoluteView = useBreakpointValueGS({ lg: '-100%', base: 20 });
  const paddingLeftAbsoluteView = useBreakpointValueGS({ lg: calcSizeForDevice(45, 15), base: 0 });
  const flexDirectionFooter = useBreakpointValueGS({ sm: 'row', base: 'column' });

  const handleCancelAgeRating = () => {
    setAgeRatingPreference(savedAgeRatingPreference);
    toggleModal();
  };

  const body = (
    <ScrollView
      style={{ flex: 1 }}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}>
      <View
        style={{
          paddingVertical: calcSizeForDevice(16, 40),
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
        <Text
          style={{
            fontFamily: 'system-ui',
            fontSize: calcSizeForDevice(28, 36),
            lineHeight: calcSizeForDevice(28, 36),
            color: '#7A8CFF',
            fontWeight: '600',
          }}>
          Classificação etária
        </Text>

        <Text
          style={{
            fontFamily: 'system-ui',
            color: '#FFFFFF',
            fontSize: calcSizeForDevice(16, 24),
            lineHeight: 22,
            marginVertical: calcSizeForDevice(16, 24),
          }}>
          Selecione a classificação etária para este perfil
        </Text>

        {!isAgeRatingPreferenceLoaded ? (
          <View style={{ paddingVertical: 20 }}>
            <IsLoading />
          </View>
        ) : (
          <View
            style={{
              flexDirection: flexDirectionContainer,
            }}>
            <Text
              style={{
                fontFamily: 'system-ui',
                fontSize: calcSizeForDevice(16, 24),
                marginTop: calcSizeForDevice(24),
                lineHeight: 22,
                color: '#FFFFFF',
              }}>
              {ageRatingPreferenceHelperText}
            </Text>

            <View style={{ flexDirection: flexDirectionView }}>
              {availableAgeRatingKeys.map((key, index) => {
                const isActive = isAgeRatingKeyActive(key);
                const ageRatingDefinition = AgeRatingDefinitions[key];

                return (
                  <Pressable
                    key={key}
                    onFocus={(e) => e.target.blur()}
                    onPress={() => setAgeRatingPreference(key)}
                    style={{
                      flex: 1,
                      flexDirection: flexDirectionItem,
                      padding: calcSizeForDevice(0, 10),
                      marginRight: calcSizeForDevice(0, 50),
                    }}>
                    <View
                      style={{
                        position: 'relative',
                        alignItems: alignItemsView,
                        justifyContent: justifyContentView,
                      }}>
                      <Pressable
                        role="button"
                        onFocus={(e) => e.target.blur()}
                        onPress={() => setAgeRatingPreference(key)}
                        style={{
                          padding: calcSizeForDevice(5),
                          marginVertical: calcSizeForDevice(20),
                        }}>
                        <View
                          style={{
                            width: calcSizeForDevice(35, 70),
                            height: calcSizeForDevice(35, 70),
                            padding: calcSizeForDevice(5, 10),
                            borderWidth: calcSizeForDevice(5),
                            position: 'relative',
                            borderColor: '#2543BE',
                            borderRadius: 999,
                          }}>
                          {isActive && (
                            <View
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 999,
                                backgroundColor: '#2543BE',
                              }}
                            />
                          )}
                        </View>
                      </Pressable>

                      {isActive && index > 0 && (
                        <View
                          style={{
                            position: 'absolute',
                            width: widthAbsoluteView,
                            height: heightAbsoluteView,
                            top: topAbsoluteView,
                            paddingTop: paddingTopAbsoluteView,
                            left: leftAbsoluteView,
                            paddingLeft: paddingLeftAbsoluteView,
                          }}>
                          <View
                            style={{ backgroundColor: '#2543BE', width: '100%', height: '100%' }}
                          />
                        </View>
                      )}
                    </View>

                    <View
                      style={{
                        flex: 1,
                        marginVertical: calcSizeForDevice(20),
                        marginLeft: calcSizeForDevice(10),
                        minHeight: 180,
                      }}>
                      <Rating size="lg" ageRatingKey={key} append="title" />
                      <Text
                        style={{
                          color: '#fff',
                          fontFamily: 'system-ui',
                          fontSize: calcSizeForDevice(16, 24),
                          lineHeight: calcSizeForDevice(24, 28),
                          marginTop: calcSizeForDevice(20),
                        }}>
                        {ageRatingDefinition.description}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </View>
        )}
      </View>
    </ScrollView>
  );

  const footer = (
    <View
      style={{
        width: '100%',
        flexDirection: flexDirectionFooter,
        alignItems: 'center',
        gap: calcSizeForDevice(16, 32),
      }}>
      <Pressable
        role="button"
        onPress={commitAgeRatingPreference}
        style={{
          backgroundColor: '#2543BE',
          paddingVertical: calcSizeForDevice(10, 30),
          alignItems: 'center',
          borderRadius: 4,
          paddingHorizontal: 20,
        }}>
        <Text
          style={{
            fontFamily: 'system-ui',
            fontSize: calcSizeForDevice(16, 24),
            fontWeight: '700',
            color: '#fff',
            lineHeight: 20,
            textTransform: 'capitalize',
          }}>
          Salvar definição
        </Text>
      </Pressable>

      <Pressable
        role="button"
        onPress={handleCancelAgeRating}
        style={({ focused, hovered }: any) => ({
          paddingVertical: calcSizeForDevice(10, 30),
          alignItems: 'center',
          paddingHorizontal: 20,
          backgroundColor: focused || hovered ? 'transparent' : undefined,
        })}>
        <Text
          style={{
            fontFamily: 'system-ui',
            fontSize: calcSizeForDevice(16, 24),
            fontWeight: '700',
            lineHeight: 20,
            color: '#839CFF',
            textTransform: 'capitalize',
          }}>
          Cancelar
        </Text>
      </Pressable>
    </View>
  );

  return (
    <BaseModal
      {...rest}
      size="full"
      modalBody={body}
      onClose={() => toggleModal()}
      modalFooter={isAgeRatingPreferenceLoaded && footer}
      modalHeader={
        <Image
          source={BandHeaderLogo}
          alt="Band Logo"
          style={{
            width: calcSizeForDevice(100, 200),
            height: calcSizeForDevice(22, 44),
          }}
        />
      }
    />
  );
};

export default ModalAgeRating;
